import * as React from "react";
import {
  Stack,
  Text,
  Separator,
  PanelType,
} from "office-ui-fabric-react";
import { IAuditLog } from "../../vm";
import CustomDrawer from "../common/CustomDrawer";
import { AUDIT_ACTION_COLOR, AUDIT_RESOURCE_DICT, childrenGap, childrenGap8 } from "../common/Constants";
import moment from "moment";

interface AuditLogDiffPanelProps {
  log: IAuditLog;
  onDismiss: () => void;
}

const AuditLogDiffPanel: React.FC<AuditLogDiffPanelProps> = ({ log, onDismiss }) => {
  const prev = log.previousState || {};
  const next = log.newState || {};

  const allKeys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)])).filter(
    k => !["__v", "id", "_id"].includes(k)
  );

  let displayKeys: string[] = [];
  if (log.action === "UPDATE") {
    if (log.changeFields && log.changeFields.length > 0) {
      // Use backend-provided changeFields if available
      displayKeys = Array.from(new Set(log.changeFields.map(f => f.split(".")[0])));
    } else {
      // Fallback: manually filter only those fields that have actual differences
      displayKeys = allKeys.filter(key => {
        const prevVal = JSON.stringify((prev as any)[key]);
        const nextVal = JSON.stringify((next as any)[key]);
        return prevVal !== nextVal;
      });
    }
  } else {
    // For CREATE or DELETE, show all relevant non-system keys
    displayKeys = allKeys;
  }

  const formatValue = (val: any): string => {
    if (val === undefined || val === null) return "—";
    if (typeof val === "object") return JSON.stringify(val, null, 2);
    return String(val);
  };

  const resourceLabel = AUDIT_RESOURCE_DICT[log.resource as keyof typeof AUDIT_RESOURCE_DICT] || log.resource;
  const actionColor = AUDIT_ACTION_COLOR[log.action] || "#323130";

  return (
    <CustomDrawer
      handelOpen={true}
      onClose={onDismiss}
      title="Audit Trail Details"
      drawerType={PanelType.medium}
      customWidth="650px"
    >
      <Stack tokens={childrenGap}>
        {/* Summary row */}
        <Stack.Item>
          <Stack horizontal tokens={childrenGap} verticalAlign="center" style={{ paddingBottom: 12, borderBottom: "1px solid #edebe9" }}>
            <Stack.Item>
              <Stack horizontal verticalAlign="center" tokens={childrenGap8}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: actionColor }} />
                <Text style={{ fontWeight: 700, color: actionColor, fontSize: 16 }}>
                  {log.action}
                </Text>
              </Stack>
            </Stack.Item>
            <Separator vertical />
            <Stack.Item grow>
              <Text style={{ fontWeight: 600, fontSize: 16 }}>{resourceLabel}</Text>
            </Stack.Item>
          </Stack>
        </Stack.Item>

        {/* Details card */}
        <Stack.Item>
          <Stack verticalAlign="center" tokens={childrenGap8} style={{ background: "#f8f9fa", borderRadius: 8 }}>
            <Stack.Item>
              <Stack horizontal verticalAlign="center" tokens={childrenGap8}>
                <Text style={{ fontSize: 13, color: "#605e5c", fontWeight: 600 }}>Performed By:</Text>
                <Text style={{ fontSize: 13, color: "#323130" }}>{`${log.userName} (${log.userEmail})` || "System"}</Text>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Stack horizontal verticalAlign="center" tokens={childrenGap8}>
                <Text style={{ fontSize: 13, color: "#605e5c", fontWeight: 600 }}>Timestamp:</Text>
                <Text style={{ fontSize: 13, color: "#323130" }}>{moment(log.createdAt).format("DD/MM/YYYY hh:mm:ss A")}</Text>
              </Stack>
            </Stack.Item>
            {log.resourceId && (
              <Stack.Item>
                <Stack horizontal verticalAlign="center" tokens={childrenGap8}>
                  <Text style={{ fontSize: 13, color: "#605e5c", fontWeight: 600 }}>Resource ID:</Text>
                  <Text style={{ fontSize: 12, color: "#0078d4", fontFamily: "monospace" }}>{log.resourceId}</Text>
                </Stack>
              </Stack.Item>
            )}
          </Stack>
        </Stack.Item>

        <Stack.Item>
          <Separator />
        </Stack.Item>

        {log.action === "UPDATE" && displayKeys.length > 0 ? (
          <Stack.Item>
            <Stack tokens={{ childrenGap: 4 }}>
              <Stack.Item>
                <Stack horizontal style={{ padding: "0 8px" }}>
                  <Text style={{ width: 140, fontWeight: 600, fontSize: 12, color: "#605e5c", textTransform: "uppercase" }}>Field</Text>
                  <Text style={{ flex: 1, fontWeight: 600, fontSize: 12, color: "#c50f1f", paddingLeft: 8, textTransform: "uppercase" }}>Before</Text>
                  <Text style={{ flex: 1, fontWeight: 600, fontSize: 12, color: "#107c10", paddingLeft: 8, textTransform: "uppercase" }}>After</Text>
                </Stack>
              </Stack.Item>
              <Stack.Item>
                <Separator />
              </Stack.Item>
              <Stack.Item>
                <Stack tokens={{ childrenGap: 4 }}>
                  {displayKeys.map(key => {
                    const prevVal = formatValue((prev as any)[key]);
                    const nextVal = formatValue((next as any)[key]);

                    return (
                      <Stack.Item key={key}>
                        <Stack
                          horizontal
                          style={{
                            background: "#fff4ce",
                            borderRadius: 6,
                            padding: "8px",
                            alignItems: "flex-start",
                            borderLeft: "4px solid #ffb900"
                          }}
                        >
                          <Text style={{ width: 136, fontWeight: 600, fontSize: 12, flexShrink: 0, wordBreak: "break-word" }}>
                            {key}
                          </Text>
                          <Text style={{ flex: 1, fontSize: 12, color: "#c50f1f", paddingLeft: 8, wordBreak: "break-all", whiteSpace: "pre-wrap" }}>
                            {prevVal}
                          </Text>
                          <Text style={{ flex: 1, fontSize: 12, color: "#107c10", paddingLeft: 8, wordBreak: "break-all", whiteSpace: "pre-wrap" }}>
                            {nextVal}
                          </Text>
                        </Stack>
                      </Stack.Item>
                    );
                  })}
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        ) : log.action === "CREATE" && Object.keys(next).length > 0 ? (
          <Stack.Item>
            <Stack tokens={{ childrenGap: 8 }}>
              <Stack.Item>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                  <Text style={{ fontWeight: 600, fontSize: 13, color: "#107c10" }}>ENTRY DETAILS</Text>
                </Stack>
              </Stack.Item>
              <Stack.Item>
                <Stack tokens={{ childrenGap: 2 }} style={{ marginTop: 8 }}>
                  {Object.entries(next).filter(([k]) => !["__v", "id", "_id"].includes(k)).map(([key, val]) => (
                    <Stack.Item key={key}>
                      <Stack horizontal style={{ padding: "6px 8px", background: "#f3f2f1", borderRadius: 4 }}>
                        <Text style={{ width: 140, fontSize: 12, color: "#605e5c", fontWeight: 600 }}>{key}</Text>
                        <Text style={{ flex: 1, fontSize: 12, color: "#107c10", paddingLeft: 8, wordBreak: "break-all" }}>
                          {formatValue(val)}
                        </Text>
                      </Stack>
                    </Stack.Item>
                  ))}
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        ) : log.action === "DELETE" && Object.keys(prev).length > 0 ? (
          <Stack.Item>
            <Stack tokens={{ childrenGap: 8 }}>
              <Stack.Item>
                <Stack horizontal verticalAlign="center">
                  <Text style={{ fontWeight: 600, fontSize: 13, color: "#c50f1f" }}>REMOVED CONTENT</Text>
                </Stack>
              </Stack.Item>
              <Stack.Item>
                <Stack tokens={{ childrenGap: 2 }} style={{ marginTop: 8 }}>
                  {Object.entries(prev).filter(([k]) => !["__v", "id", "_id"].includes(k)).map(([key, val]) => (
                    <Stack.Item key={key}>
                      <Stack horizontal style={{ padding: "6px 8px", background: "#f3f2f1", borderRadius: 4 }}>
                        <Text style={{ width: 140, fontSize: 12, color: "#605e5c", fontWeight: 600 }}>{key}</Text>
                        <Text style={{ flex: 1, fontSize: 12, color: "#c50f1f", paddingLeft: 8, wordBreak: "break-all" }}>
                          {formatValue(val)}
                        </Text>
                      </Stack>
                    </Stack.Item>
                  ))}
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        ) : (
          <Stack.Item>
            <Stack horizontalAlign="center" style={{ padding: 40 }}>
              <Text style={{ color: "#605e5c", fontSize: 13, fontStyle: "italic" }}>No specific data changes were captured for this activity.</Text>
            </Stack>
          </Stack.Item>
        )}

        <Stack.Item>
          <Separator />
        </Stack.Item>

        {/* Request body summary */}
        {log.requestBody && Object.keys(log.requestBody).length > 0 && (
          <Stack.Item>
            <Stack tokens={{ childrenGap: 4 }}>
              <Stack.Item>
                <Text style={{ fontWeight: 600, fontSize: 12, color: "#605e5c", textTransform: "uppercase" }}>Original Request Payload</Text>
              </Stack.Item>
              <Stack.Item>
                <pre style={{
                  fontSize: 11,
                  background: "#201f1e",
                  color: "#d1d0cf",
                  padding: 12,
                  borderRadius: 8,
                  overflow: "auto",
                  maxHeight: 250,
                }}>
                  {JSON.stringify(log.requestBody, null, 2)}
                </pre>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}
      </Stack>
    </CustomDrawer>
  );
};

export default AuditLogDiffPanel;
